import React, { useMemo } from "react";
import { Avatar, AvatarGroup, Box, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";

function initialsFromName(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
}

function avatarColor(seed, primary) {
  const s = String(seed || "");
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  const palette = [
    primary || "#1a73e8",
    "#188038",
    "#e37400",
    "#d93025",
    "#9334e6",
    "#007b83",
  ];
  return palette[h % palette.length];
}

/**
 * Bloco Meet-like: quem já está na chamada (tela de pré-entrada).
 */
export default function VideoPreJoinParticipants({
  participants = [],
  primary,
  waitingLabel = "Ninguém na chamada ainda",
  oneInCallLabel = "{{name}} já está na chamada",
  manyInCallLabel = "{{count}} pessoas na chamada",
  andOthersLabel = "e mais {{count}}",
}) {
  const list = useMemo(
    () => (Array.isArray(participants) ? participants.filter((p) => p?.displayName || p?.identity) : []),
    [participants]
  );

  const headline = useMemo(() => {
    if (!list.length) return waitingLabel;
    if (list.length === 1) {
      return String(oneInCallLabel).replace(
        "{{name}}",
        list[0].displayName || "Alguém"
      );
    }
    return String(manyInCallLabel).replace("{{count}}", String(list.length));
  }, [list, waitingLabel, oneInCallLabel, manyInCallLabel]);

  const namesLine = useMemo(() => {
    if (list.length <= 1) return null;
    const shown = list.slice(0, 3).map((p) => p.displayName || "Participante");
    const rest = list.length - shown.length;
    let text = shown.join(", ");
    if (rest > 0) {
      text += ` ${String(andOthersLabel).replace("{{count}}", String(rest))}`;
    }
    return text;
  }, [list, andOthersLabel]);

  return (
    <Box
      sx={{
        mb: 1.5,
        p: 1.5,
        borderRadius: 2,
        border: `1px solid ${alpha("#000", 0.08)}`,
        bgcolor: "#f8f9fa",
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        {list.length ? (
          <AvatarGroup
            max={4}
            sx={{
              "& .MuiAvatar-root": {
                width: 36,
                height: 36,
                fontSize: "0.75rem",
                fontWeight: 700,
                border: "2px solid #f8f9fa",
              },
            }}
          >
            {list.map((p) => {
              const name = p.displayName || "Participante";
              return (
                <Avatar
                  key={p.identity || p.sid || name}
                  alt={name}
                  src={p.avatarUrl || undefined}
                  title={name}
                  sx={{
                    bgcolor: avatarColor(p.identity || name, primary),
                  }}
                >
                  {initialsFromName(name)}
                </Avatar>
              );
            })}
          </AvatarGroup>
        ) : (
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: alpha(primary || "#1a73e8", 0.12),
              color: primary || "#1a73e8",
            }}
          >
            <GroupsOutlinedIcon sx={{ fontSize: 20 }} />
          </Avatar>
        )}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontSize: "0.9rem",
              fontWeight: 600,
              color: "#202124",
              lineHeight: 1.35,
            }}
          >
            {headline}
          </Typography>
          {namesLine ? (
            <Typography
              sx={{
                mt: 0.25,
                fontSize: "0.78rem",
                color: "text.secondary",
                lineHeight: 1.4,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {namesLine}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Box>
  );
}
